import {Map, Set, List, Record, type RecordOf} from 'immutable';

import {type CollectionRecordType as Collection} from '@packages/systems/dynamo/records/CollectionRecord';
import type {FieldType as Field} from '@packages/systems/dynamo/records/Field';
import type {ItemRecordType} from '@packages/systems/core/records/ItemRecord';
import type {Locale, LocaleId} from '@packages/systems/localization-types';

import {weakMemo} from '@packages/utilities/memo';
import {__DEPRECATED__getCMSItemsFromStore} from '@packages/systems/cms/client-selectors';

export type CollectionsType = Map<string, Collection>;
export type ItemId = string;
export type ItemsType = Map<ItemId, ItemRecordType>;
export type LocalizedItemsType = Map<LocaleId, ItemsType>;
export type ItemsIndexType = Map<string, Set<string>>;
export type FieldsType = Map<string, Field>;
export type ScheduleItemsType = ScheduleItem[];
export type ItemCountByCollectionIdType = Map<string, number>;
export type LocalizedItemCountByLocaleType = Map<
  string,
  ItemCountByCollectionIdType
>;

export type ScheduleItem = {
  itemId: string;
  localeId?: string | null | undefined;
  collectionId: string;
  scheduledTime: string;
  scheduledBy: string;
  publishError: string;
};

export type CollectionStateItemsFor = (
  arg1: Collection,
  options?:
    | {
        locale: Locale;
      }
    | {
        allLocales: boolean;
      }
) => List<ItemRecordType>;

export type CollectionStateShape = {
  collections: CollectionsType;
  items: ItemsType;
  localizedItems: LocalizedItemsType;
  itemIndex: ItemsIndexType;
  fields: FieldsType;
  scheduledItems: ScheduleItemsType;
  itemsFor: CollectionStateItemsFor;
  __DEPRECATED__itemCountByCollectionId: ItemCountByCollectionIdType;
  localizedItemCountByLocaleId?: LocalizedItemCountByLocaleType;
};

export type CollectionStateType = RecordOf<CollectionStateShape>;

const getAll = weakMemo((coll: ItemsType) =>
  weakMemo((keys: Set<string>): List<ItemRecordType> => {
    return keys
      .map<any>((k) => coll.get(k))
      .filter(Boolean)
      .toList();
  })
);

export const mapCmsItemsById = weakMemo(
  (items: List<ItemRecordType>): ItemsType =>
    Map<ItemId, ItemRecordType>(items.map((item) => [item.get('_id'), item]))
);

export const CollectionState = Record<CollectionStateShape>({
  collections: Map(),
  items: Map(),
  localizedItems: Map(),
  itemIndex: Map(), // collection_id -> Set(...item_ids)
  scheduledItems: [] as ScheduleItemsType,
  fields: Map(),
  itemsFor: function itemsFor(
    collection: Collection,
    options?:
      | {
          locale: Locale;
        }
      | {
          allLocales: boolean;
        }
  ) {
    if (!options) {
      const itemIds = this.itemIndex.get(String(collection?._id));
      if (!itemIds) return List();
      return getAll(this.items)(itemIds);
    }

    if ('locale' in options) {
      const {locale} = options;
      const {cmsId} = locale;

      const itemIds = (
        this.localizedItems.get(String(cmsId ?? '')) || Map()
      ).reduce<string[]>((acc, item) => {
        if (!acc) {
          // TS check
          return [];
        }

        if (item.get('_cid') === collection?._id) {
          acc.push(item.get('_id'));
        }
        return acc;
      }, []);

      return __DEPRECATED__getCMSItemsFromStore(this, itemIds, locale);
    }

    if ('allLocales' in options) {
      const foundItems = List<ItemRecordType>().asMutable();
      const allLocalizedItems: LocalizedItemsType = this.localizedItems;

      // We'll go ahead and add the non-localized items
      const itemsByIndex = this.itemIndex.get(String(collection._id));
      if (itemsByIndex) {
        // Warning: When working on https://webflow.atlassian.net/browse/OP-179 we were seeing items with
        //   non-empty `_locale` being present in `nonLocalizedItems`, which makes its name highly misleading.
        const nonLocalizedItems = getAll(this.items)(itemsByIndex);

        if (nonLocalizedItems) {
          foundItems.push(...nonLocalizedItems);
        }
      }

      for (const [, localizedItemsByLocale] of allLocalizedItems) {
        localizedItemsByLocale.forEach((localizedItem) => {
          if (localizedItem.get('_cid') === collection?._id) {
            // In https://webflow.atlassian.net/browse/OP-179 there were situations when an item was added twice
            //   to `foundItems`: one instance was coming from `nonLocalizedItems` above, and the other was added
            //   here, from `localizedItemsByLocale`. While there is a chance we do not need `nonLocalizedItems`
            //   here at all, removing it might require an extra effort on testing against regressions across all
            //   the places this code is used. Instead, we decided for a simple workaround (and not addressing
            //   the core issues) by removing the duplicate here below.
            // Consider removing this de-duplication code once removal of `itemIndex` is done
            //   (https://webflow.atlassian.net/browse/OP-151).
            const duplicateIndex = foundItems.findIndex(
              (item) =>
                item.get('_id') === localizedItem.get('_id') &&
                item.get('_locale') === localizedItem.get('_locale')
            );
            if (duplicateIndex >= 0) {
              foundItems.delete(duplicateIndex);
            }

            foundItems.push(localizedItem);
          }
        });
      }

      return foundItems.asMutable();
    }

    return List();
  },
  __DEPRECATED__itemCountByCollectionId: Map(),
  localizedItemCountByLocaleId: Map(),
});
